// @ts-check
// ── The sidebar (Clayton, 2026-10-12) ─────────────────────────────────────
// Built from the approved prototype, mockups/crm-sidebar-prototype.html. The
// markup is static in index.html (aside#sidebar); this file gives it its
// behaviour:
//   - the [ key opens and closes the menu, the same as the panel button
//     beside the logo (side-menus.js toggleSidebarCollapse, so the Lead
//     Engine's visit-only rule still holds);
//   - in the narrow menu, the names come from ONE floating tooltip on <body>.
//     The old CSS ::after tooltips were cut off by the menu's scroll area;
//   - "Search or ask" opens the search window (⌘K);
//   - the + menu: New client, New event, New program, New message;
//   - the account row (initials, name, business) and its menu: Settings,
//     Status, What's new, Admin Panel, Sign Out. "Something new" and the
//     Admin Panel's warning also show on the row, since their own rows sit
//     inside a closed menu.
//
// ⚠️ NEW EVENT, NEW PROGRAM AND NEW MESSAGE PRESS THE PAGE'S OWN BUTTON (the
// Calendar's New, Builder's + New Program, the corner chat's pencil) once it is
// there. Those pages load lazily and own their state; going through their own
// button means nothing of theirs changes and a create starts exactly the way a
// click there does.

import { toggleSidebarCollapse } from './side-menus.js';
import { currentView } from './state.js';
import { pressWhenThere } from './press-when-there.js';

/** @type {{ openAddModal: () => unknown, showView: (name: string) => unknown }} */
let deps = { openAddModal: () => {}, showView: () => {} };
/** @type {AbortController | null} */
let wiring = null;
/** @type {HTMLElement | null} */
let tip = null;
/** @type {MutationObserver[]} */
let watchers = [];

const $ = (/** @type {string} */ sel) => /** @type {HTMLElement | null} */ (document.querySelector(sel));
const sidebar = () => $('#sidebar');
const narrow = () => !!sidebar()?.classList.contains('collapsed');

/**
 * Give the sidebar its behaviour. Safe to call once at boot.
 * @param {{ openAddModal?: () => unknown, showView?: (name: string) => unknown }} [d]
 */
export function initSidebar(d = {}) {
  teardownSidebarForTests();
  deps = {
    openAddModal: d.openAddModal || (() => {}),
    showView: d.showView || ((name) => /** @type {any} */ (window)._navigation?.showView?.(name)),
  };
  wiring = new AbortController();
  const on = { signal: wiring.signal };
  const sb = sidebar();
  if (!sb) return;

  document.addEventListener('keydown', onKey, on);
  sb.addEventListener('mouseover', onHover, on);
  sb.addEventListener('focusin', onHover, on);
  sb.addEventListener('mouseleave', hideTip, on);
  sb.addEventListener('focusout', hideTip, on);
  sb.addEventListener('click', onClick, on);
  document.addEventListener('click', onOutside, { ...on, capture: true });
  window.addEventListener('resize', closeMenus, on);

  // "Something new" and the Admin Panel's warning, shown on the account row too.
  mirror('#whats-new-dot', (el) => !el.classList.contains('hidden'), 'has-news');
  mirror('#btn-admin-panel', (el) => el.classList.contains('admin-health-active'), 'has-alert');
}

/** For the tests: take every listener and watcher off again. */
export function teardownSidebarForTests() {
  wiring?.abort();
  wiring = null;
  watchers.forEach((w) => w.disconnect());
  watchers = [];
  tip?.remove();
  tip = null;
}

/**
 * The account row: the coach's initials, name and business.
 * @param {{ name?: string | null, business?: string | null }} who
 */
export function setSidebarAccount({ name, business }) {
  const row = $('#sb-account');
  if (!row) return;
  const shown = String(name || '').trim();
  const words = shown.split(/\s+/).filter(Boolean);
  const initials = words.length >= 2 ? words[0][0] + words[words.length - 1][0] : shown.slice(0, 2);
  const set = (/** @type {string} */ sel, /** @type {string} */ text) => { const el = row.querySelector(sel); if (el) el.textContent = text; };
  set('.sb-avatar-initials', initials.toUpperCase());
  set('.sb-who-name', shown);
  set('.sb-who-business', String(business || '').trim());
  row.setAttribute('data-tooltip', shown || 'Account');
  row.setAttribute('aria-label', shown ? `${shown}, account menu` : 'Account menu');
}

// ── The [ key ─────────────────────────────────────────────────────

/** @param {KeyboardEvent} e */
function onKey(e) {
  if (e.key === 'Escape') { closeMenus(); return; }
  if (e.key !== '[' || e.metaKey || e.ctrlKey || e.altKey) return;
  const t = /** @type {HTMLElement | null} */ (e.target instanceof HTMLElement ? e.target : document.activeElement);
  if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
  e.preventDefault();
  closeMenus();
  hideTip();
  toggleSidebarCollapse();
}

// ── The narrow menu's tooltip ─────────────────────────────────────

/** @param {Event} e */
function onHover(e) {
  const el = /** @type {HTMLElement | null} */ ((/** @type {HTMLElement} */ (e.target)).closest?.('[data-tooltip]') || null);
  // Wide, the names are on screen; only the panel button has a word to add.
  if (!el || el.closest('.sb-account-menu, .sb-new-menu') || (!narrow() && el.id !== 'sidebar-toggle')) { hideTip(); return; }
  if (el.hidden) { hideTip(); return; }
  showTip(el);
}

/** @param {HTMLElement} el */
function showTip(el) {
  // Read fresh every time: wake-indicator.js and the Admin Panel badge rewrite it.
  let words = el.getAttribute('data-tooltip') || '';
  if (el.id === 'sidebar-toggle') words = narrow() ? 'Expand the menu' : 'Collapse the menu';
  if (!words) { hideTip(); return; }
  if (!tip) {
    tip = document.createElement('div');
    tip.className = 'sb-tip';
    tip.setAttribute('role', 'tooltip');
    document.body.appendChild(tip);
  }
  tip.textContent = words;
  if (el.id === 'sidebar-toggle') {
    const k = document.createElement('kbd');
    k.textContent = '[';
    tip.appendChild(k);
  }
  const r = el.getBoundingClientRect();
  const right = sidebar()?.getBoundingClientRect().right ?? r.right;
  tip.style.left = `${Math.round((narrow() ? right : r.right) + 8)}px`;
  tip.style.top = `${Math.round(r.top + r.height / 2)}px`;
  tip.classList.add('on');
}

function hideTip() { tip?.classList.remove('on'); }

// ── The menus ─────────────────────────────────────────────────────

/** @param {MouseEvent} e */
function onClick(e) {
  const t = /** @type {HTMLElement} */ (e.target);
  if (t.closest('#sb-search')) { hideTip(); openSearch(); return; }
  if (t.closest('#sb-new')) { hideTip(); toggleMenu('#sb-new', '#sb-new-menu'); return; }
  if (t.closest('#sb-account')) { hideTip(); toggleMenu('#sb-account', '#sb-account-menu'); return; }
  const make = /** @type {HTMLElement | null} */ (t.closest('#sb-new-menu [data-new]'));
  if (make) { closeMenus(); startNew(make.dataset.new || ''); return; }
  // A pick in the account menu (Settings, Status, What's new, Admin Panel, Sign Out)
  // does its own thing; the menu just closes.
  if (t.closest('#sb-account-menu .sb-menu-item')) closeMenus();
}

/** @param {MouseEvent} e */
function onOutside(e) {
  const t = /** @type {HTMLElement} */ (e.target);
  if (t.closest('#sb-new, #sb-new-menu, #sb-account, #sb-account-menu')) return;
  closeMenus();
}

/** @param {string} btnSel @param {string} menuSel */
function toggleMenu(btnSel, menuSel) {
  const btn = $(btnSel); const menu = $(menuSel);
  if (!btn || !menu) return;
  const open = !menu.classList.contains('open');
  closeMenus();
  if (!open) return;
  menu.classList.add('open');
  btn.setAttribute('aria-expanded', 'true');
  placeMenu(btn, menu);
  /** @type {HTMLElement | null} */ (menu.querySelector('.sb-menu-item:not(.hidden):not([hidden])'))?.focus?.({ preventScroll: true });
}

/**
 * Wide: the account menu sits in the sidebar above its row (CSS), and the + menu
 * floats just under the top row, as wide as the sidebar's inside. Narrow, there
 * is no room: either floats to the right of the menu, level with its button.
 * @param {HTMLElement} btn @param {HTMLElement} menu
 */
function placeMenu(btn, menu) {
  menu.style.left = ''; menu.style.top = ''; menu.style.bottom = '';
  const r = btn.getBoundingClientRect();
  const side = sidebar()?.getBoundingClientRect();
  if (!narrow()) {
    if (menu.id !== 'sb-new-menu' || !side) return;
    menu.style.left = `${Math.round(side.left + 10)}px`;
    menu.style.top = `${Math.round(r.bottom + 6)}px`;
    return;
  }
  menu.style.left = `${Math.round((side?.right ?? r.right) + 8)}px`;
  if (menu.id === 'sb-account-menu') menu.style.bottom = `${Math.round(window.innerHeight - r.bottom)}px`;
  else menu.style.top = `${Math.round(r.top)}px`;
}

function closeMenus() {
  for (const [b, m] of [['#sb-new', '#sb-new-menu'], ['#sb-account', '#sb-account-menu']]) {
    $(m)?.classList.remove('open');
    $(b)?.setAttribute('aria-expanded', 'false');
  }
}

// ── What each thing does ──────────────────────────────────────────

/** The search window (⌘K): the same one the shortcut opens. */
function openSearch() {
  const overlay = $('#gs-modal-overlay');
  if (!overlay) return;
  overlay.classList.remove('hidden');
  /** @type {HTMLInputElement | null} */ ($('#gs-modal-input'))?.focus();
}

/** @param {string} what */
function startNew(what) {
  if (what === 'client') { deps.openAddModal(); return; }
  if (what === 'event') {
    deps.showView('calendar');
    pressWhenThere('#view-calendar [data-c2-new]');
    return;
  }
  if (what === 'program') {
    // Builder draws the active tab, so the Workout tab is set before it opens.
    document.querySelectorAll('.builder-tab-btn').forEach((b) => b.classList.toggle('active', /** @type {HTMLElement} */ (b).dataset.builderTab === 'workout'));
    document.querySelectorAll('.builder-tab-panel').forEach((p) => p.classList.toggle('active', p.id === 'builder-tab-workout'));
    deps.showView('builder');
    // + New Program works once Builder has loaded: the program list is drawn after it.
    pressWhenThere('#btn-new-program', '#builder-program-list > *');
    return;
  }
  if (what === 'message') {
    // The Inbox page hides the corner chat and has its own picker.
    if (currentView === 'inbox') {
      if ($('#inbox-compose-panel')?.classList.contains('hidden')) /** @type {any} */ (window)._toggleNewConversation?.();
      return;
    }
    if (!$('#md-root .md-list')) /** @type {HTMLElement | null} */ ($('#md-root .md-launch'))?.click();
    pressWhenThere('#md-root .md-list [data-act="compose"]');
  }
}

// ── Mirrors on the account row ────────────────────────────────────

/**
 * Keep a class on the account row in step with another element's state.
 * @param {string} sel @param {(el: HTMLElement) => boolean} test @param {string} cls
 */
function mirror(sel, test, cls) {
  const el = $(sel); const row = $('#sb-account');
  if (!el || !row) return;
  const sync = () => row.classList.toggle(cls, test(el));
  sync();
  const w = new MutationObserver(sync);
  w.observe(el, { attributes: true, attributeFilter: ['class'] });
  watchers.push(w);
}
